import { authFetch } from '../../utils/cloudFunctionsAuth';
import type {
  MonthlySupportOverview, SaveSupportPlanInput, SaveSupportPlanResult,
  EnsureSupportMonthResult, UpdateSupportDutyInput, UpdateSupportDutyResult,
} from './types';

const ENDPOINT = 'https://us-central1-web-construct-app.cloudfunctions.net/manageMonthlySupport';

export class MonthlySupportApiError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'MonthlySupportApiError';
  }
}

async function request<T>(action: string, input: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
  let response: Response;
  try {
    response = await authFetch(ENDPOINT, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...input }), signal,
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new MonthlySupportApiError('The monthly support service could not be reached. Billing sources remain visible; service records are unavailable.');
  }
  const body = await response.json().catch(() => null);
  if (!response.ok || body?.ok !== true) {
    const message = response.status === 401 || response.status === 403
      ? 'Your account cannot access monthly support management.'
      : typeof body?.error === 'string' ? body.error
      : response.status === 404 ? 'The monthly support service is not deployed yet. Billing sources remain visible; service records are unavailable.'
      : 'Monthly support records could not be loaded or saved.';
    throw new MonthlySupportApiError(message, response.status);
  }
  return body as T;
}

export const monthlySupportApi = {
  overview: (month: string, signal?: AbortSignal) => request<MonthlySupportOverview>('overview', { month }, signal),
  savePlan: (plan: SaveSupportPlanInput) => request<SaveSupportPlanResult>('savePlan', { plan }),
  ensureMonth: (planId: string, month: string) => request<EnsureSupportMonthResult>('ensureMonth', { planId, month }),
  updateDuty: (input: UpdateSupportDutyInput) => request<UpdateSupportDutyResult>('updateDuty', { ...input }),
};
